import React from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useAuthDrawer } from '../context/AuthDrawerContext';
import { useSiteFeatureFlags } from '../hooks/useSiteFeatureFlags';

// Drop the real photo and logo at these paths (public/images/gosoup/).
const HERO_IMG = '/images/gosoup/hero.jpg';
const LOGO_IMG = '/images/gosoup/daily-soup-logo.png';

const NAV = [
  { label: 'Home', to: '/', active: true },
  // Server-rendered page, so a plain link (full page load); hidden while the directory flag is off.
  { label: 'Self Help Directory', to: '/directory', directory: true },
  { label: 'How to use', to: '/how-to-use' },
  { label: 'Who we are', to: '/about' },
  { label: 'Donate', to: '/donate' },
];

const DailySoupLogo = () => (
  <div className="flex flex-col items-center" data-testid="daily-soup-logo">
    <img
      src={LOGO_IMG}
      alt="Daily Soup - The Self Help Directory"
      className="h-40 md:h-52 object-contain"
      onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'block'; }}
    />
    <div style={{ display: 'none' }} className="text-center text-white">
      <div className="text-5xl md:text-6xl" style={{ fontFamily: "'Caveat', 'Brush Script MT', cursive", transform: 'rotate(-4deg)' }}>Daily Soup</div>
      <div className="text-xs tracking-[0.3em] italic mt-2 border-t border-white/60 pt-2">THE SELF HELP DIRECTORY</div>
    </div>
  </div>
);

const GoSoupHome = () => {
  const { openAuth } = useAuthDrawer();
  const { _loaded: flagsLoaded, directory_enabled } = useSiteFeatureFlags();
  const nav = NAV.filter((n) => !n.directory || (flagsLoaded && directory_enabled !== false));

  return (
    <div
      className="relative min-h-screen w-full flex flex-col text-white"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.25),rgba(0,0,0,0.25)), url(${HERO_IMG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        fontFamily: "'Open Sans', 'Segoe UI', system-ui, sans-serif",
      }}
      data-testid="gosoup-home"
    >
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Caveat:wght@500&family=Open+Sans:wght@300;400;600&display=swap" />

      <header className="relative z-10 flex items-center justify-between px-6 md:px-8 h-[74px] bg-black/50">
        <Link to="/" className="text-4xl text-white" style={{ fontFamily: "'Caveat', cursive" }} data-testid="gosoup-logo">Go Soup</Link>
        <nav className="hidden md:flex items-center gap-8 text-[15px] font-semibold">
          {nav.map((n) => {
            const cls = `pb-1 border-b-2 ${n.active ? 'border-red-500 text-white' : 'border-transparent text-white/85 hover:text-white'}`;
            return n.directory
              ? <a key={n.label} href={n.to} className={cls}>{n.label}</a>
              : <Link key={n.label} to={n.to} className={cls}>{n.label}</Link>;
          })}
          <button type="button" aria-label="Search" className="text-white/85 hover:text-white"><Search size={20} /></button>
        </nav>
      </header>

      <main className="relative z-10 flex-1 flex items-center px-4 md:px-14 py-10">
        <section
          className="w-full max-w-[680px] border border-white/90 bg-black/30 backdrop-blur-sm px-8 md:px-14 py-10 md:py-12"
          data-testid="gosoup-hero-box"
        >
          <DailySoupLogo />
          <div className="mt-8 space-y-5 text-[17px] md:text-lg font-light leading-relaxed">
            <p>Daily Soup Kitchen Directory. Gosoup.org is an all Volunteer Non-profit Organization Offering Assistance to everyone in need.</p>
            <p>GetSoup is an easy to use application to help locate services in your local area — and even outside of your local area. This page explains some of the ways in which GetSoup can help.</p>
            <p>Use the Self Help Directory link above to locate a local Soup Kitchen or other type of Service Provider.</p>
          </div>
          <div className="mt-9 grid grid-cols-2 gap-5">
            <button type="button" onClick={() => openAuth('login')} data-testid="gosoup-login"
              className="border border-white/90 py-4 text-base font-light tracking-wide uppercase hover:bg-white/15 transition">Log in</button>
            <button type="button" onClick={() => openAuth('register')} data-testid="gosoup-register"
              className="border border-white/90 py-4 text-base font-light tracking-wide uppercase hover:bg-white/15 transition">Register</button>
          </div>
        </section>
      </main>

      <footer className="relative z-10 bg-black/60 text-center text-sm py-4">© 2026 GOSOUP. Powered by A2G</footer>
    </div>
  );
};

export default GoSoupHome;
